import { PartnerSettingValue } from '@provision/entity-types';
import { stableStringify } from '@provision/formatting-utils';
import { useCallback, useMemo, useState } from 'react';
import {
  PartnerSetting,
  useManagePartnerSettingsValue,
  usePartnerSettings,
} from 'src/graphql/entities/partner-setting';
import {
  SlaRoutingPolicy,
  SlaThresholdMap,
} from 'src/pages/Settings/preferences/hooks/sla-routing.constants';
import { useNotifications } from 'src/utils/hooks';

export {
  SLA_THRESHOLDS,
  SLA_THRESHOLD_LABELS,
  ORDERED_SLA_THRESHOLDS,
} from 'src/pages/Settings/preferences/hooks/sla-routing.constants';
export type {
  VoiceRoutingAction,
  SlaThresholdMap,
  TimeBasedRoutingMap,
  EmailChannelPolicy,
  SlaRoutingPolicy,
} from 'src/pages/Settings/preferences/hooks/sla-routing.constants';

const EMPTY_POLICY: SlaRoutingPolicy = {};

export function useSlaRoutingPolicy(partnerId: number | null) {
  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const [pendingPolicy, setPendingPolicy] = useState<SlaRoutingPolicy | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data, loading, error } = usePartnerSettings({
    partnerSetting: PartnerSetting.SlaRoutingPolicy,
    partnerId,
  });

  const { createPartnerSettingsValue, updatePartnerSettingsValue } =
    useManagePartnerSettingsValue(partnerId);

  const records = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data;
  }, [data]);

  // Partner's own record (for updates)
  const partnerPolicyRecord = useMemo(
    () => records.find((r) => r.partnerId === partnerId) ?? null,
    [records, partnerId],
  );

  // Effective policy: partner's own or platform fallback (for reading)
  const effectivePolicyRecord = useMemo(() => {
    if (!records.length) return null;
    return partnerPolicyRecord ?? records.find((r) => r.partnerId !== partnerId) ?? null;
  }, [records, partnerPolicyRecord, partnerId]);

  const savedPolicy = useMemo((): SlaRoutingPolicy => {
    if (!effectivePolicyRecord?.value) return EMPTY_POLICY;
    try {
      return JSON.parse(effectivePolicyRecord.value) as SlaRoutingPolicy;
    } catch {
      return EMPTY_POLICY;
    }
  }, [effectivePolicyRecord]);

  const policy = pendingPolicy ?? savedPolicy;

  const hasUnsavedChanges = useMemo(() => {
    if (!pendingPolicy) return false;
    return stableStringify(pendingPolicy) !== stableStringify(savedPolicy);
  }, [pendingPolicy, savedPolicy]);

  const savePolicy = useCallback(async () => {
    if (!pendingPolicy || loading) return;
    setIsSaving(true);
    const jsonValue = JSON.stringify(pendingPolicy);

    try {
      // Only update if partner has their own record; otherwise create new
      if (partnerPolicyRecord?.partnerSettingValueId) {
        await updatePartnerSettingsValue({
          partnerSettingValueId: partnerPolicyRecord.partnerSettingValueId,
          partnerSettingId: PartnerSetting.SlaRoutingPolicy,
          value: jsonValue,
        });
      } else {
        await createPartnerSettingsValue({
          partnerSettingId: PartnerSetting.SlaRoutingPolicy,
          value: jsonValue,
        } as Partial<PartnerSettingValue>);
      }
      showSuccessAlert('SLA routing policy saved');
      // Don't refetch - rely on Apollo cache updates from mutation to prevent UI flash
      setPendingPolicy(null);
    } catch (err: unknown) {
      console.error('Failed to save SLA routing policy', err);
      showErrorAlert('Failed to save SLA routing policy');
    } finally {
      setIsSaving(false);
    }
  }, [
    pendingPolicy,
    partnerPolicyRecord,
    updatePartnerSettingsValue,
    createPartnerSettingsValue,
    showSuccessAlert,
    showErrorAlert,
    loading,
  ]);

  const discardChanges = useCallback(() => {
    setPendingPolicy(null);
  }, []);

  const updateEmailPolicy = useCallback(
    (
      emailType: 'support' | 'membership',
      timeBlock: 'during_business_hours' | 'after_business_hours',
      thresholdMap: SlaThresholdMap,
    ) => {
      if (loading) return;
      const currentPolicy = pendingPolicy ?? savedPolicy;
      const updatedPolicy: SlaRoutingPolicy = {
        ...currentPolicy,
        email: {
          ...currentPolicy.email,
          [emailType]: {
            ...currentPolicy.email?.[emailType],
            [timeBlock]: thresholdMap,
          },
        },
      };
      setPendingPolicy(updatedPolicy);
    },
    [pendingPolicy, savedPolicy, loading],
  );

  const updateVoicePolicy = useCallback(
    (
      timeBlock: 'during_business_hours' | 'after_business_hours',
      thresholdMap: SlaThresholdMap,
    ) => {
      if (loading) return;
      const currentPolicy = pendingPolicy ?? savedPolicy;
      const updatedPolicy: SlaRoutingPolicy = {
        ...currentPolicy,
        voice: {
          ...currentPolicy.voice,
          [timeBlock]: thresholdMap,
        },
      };
      setPendingPolicy(updatedPolicy);
    },
    [pendingPolicy, savedPolicy, loading],
  );

  return {
    policy,
    savedPolicy,
    policyRecord: partnerPolicyRecord,
    loading,
    error,
    isSaving,
    hasUnsavedChanges,
    savePolicy,
    discardChanges,
    updateEmailPolicy,
    updateVoicePolicy,
  };
}
